/**
 * Asking a question about selected text.
 *
 * THIS REPLACED A `window.prompt`, which could not hold a picker and could not show what it was
 * about. A browser prompt in a controlled document editor is also a dialog nobody can style,
 * nobody can paste into comfortably, and that looks like the page has gone wrong.
 */
import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import MentionField from "./MentionField";
import { stillNamed } from "./mentions";
import type { Person } from "./mentions";
import { CHROME_FONT } from "./EditorToolbar";
import { Grey5, Grey17 } from "../../theme/styles";

export default function CommentComposer({ anchorText, people, onCancel, onSend }: {
    /** The words the question is about. Shown, because a question without them reads as a riddle. */
    anchorText: string;
    people: (search: string) => Promise<Person[]>;
    onCancel: () => void;
    onSend: (body: string, mentions: string[]) => Promise<void>;
}) {
    const [text, setText] = React.useState("");
    const [chosen, setChosen] = React.useState<Person[]>([]);
    const [busy, setBusy] = React.useState(false);

    const send = async () => {
        const body = text.trim();
        if (!body || busy) return;
        setBusy(true);
        try {
            // AGAINST THE TEXT BEING SENT, not against what was picked earlier -- a name deleted
            // again is a mention withdrawn.
            await onSend(body, stillNamed(body, chosen).map(p => p.AccountUserUSID));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onCancel}>
            <DialogTitle sx={{ fontSize: 16, fontFamily: CHROME_FONT }}>Comment</DialogTitle>
            <DialogContent>
                {!!anchorText && (
                    <Box sx={{ mb: 1.5, pl: 1.25, borderLeft: `3px solid ${Grey5}` }}>
                        <Typography sx={{ fontSize: 12.5, color: Grey17, fontFamily: CHROME_FONT,
                                          fontStyle: "italic" }}>
                            “{anchorText.slice(0, 200)}{anchorText.length > 200 ? "…" : ""}”
                        </Typography>
                    </Box>
                )}
                <MentionField autoFocus minRows={3}
                              placeholder="What would you like to ask? Type @ to name somebody"
                              value={text} onChange={setText}
                              chosen={chosen} onChosen={setChosen}
                              people={people}/>
                <Typography sx={{ fontSize: 11, color: Grey5, mt: 0.75, fontFamily: CHROME_FONT }}>
                    Anybody you name has to be able to see this document.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} sx={{ fontSize: 13, color: Grey5, fontFamily: CHROME_FONT }}>
                    Cancel
                </Button>
                <Button onClick={() => void send()} disabled={!text.trim() || busy}
                        sx={{ fontSize: 13, fontFamily: CHROME_FONT }}>
                    Comment
                </Button>
            </DialogActions>
        </Dialog>
    );
}
